// Замещения (contracts/api.md, «Назначения ролей и замещения»; FR-018 – FR-021).
import { z } from '../kernel/zod.ts';
import { SubstitutionSchema, SubstitutionStateSchema } from '../access/assignment.ts';
import type { SubstitutionState } from '../access/assignment.ts';
import { IdSchema } from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { PageQuerySchema, pageSchema, sortSchema } from '../kernel/page.ts';
import type { SortValue } from '../kernel/page.ts';
import { datesInOrder } from './assignments.ts';

export const substitutionSortFields = ['validFrom', 'validTo'] as const;
export type SubstitutionSort = SortValue<(typeof substitutionSortFields)[number]>;

/**
 * GET /api/substitutions (T064): администратор — все, владелец и заместитель — свои (в сеансе,
 * даже без ролей). q — часть ФИО владельца или заместителя; state; sort — по сроку, со знаком
 * «-» по убыванию; без sort — по состоянию (defaultSubstitutionSort); курсор.
 */
export const SubstitutionsQuerySchema = z.object({
  ...PageQuerySchema.shape,
  q: z.string().trim().max(200).optional(),
  state: SubstitutionStateSchema.optional(),
  sort: sortSchema(substitutionSortFields).optional(),
});
export type SubstitutionsQuery = z.input<typeof SubstitutionsQuerySchema>;

/**
 * Сортировка без ?sort=: запланированные — по началу, действующие — по концу (ближайшие сверху),
 * закончившиеся и отменённые — свежие сверху; все вместе — новые по началу сверху.
 */
export function defaultSubstitutionSort(state: SubstitutionState | undefined): SubstitutionSort {
  switch (state) {
    case 'planned':
      return 'validFrom';
    case 'active':
      return 'validTo';
    case 'ended':
    case 'cancelled':
      return '-validTo';
    case undefined:
      return '-validFrom';
  }
}

/**
 * POST /api/substitutions. Прошедшие даты, сам себе, роль «сопровождение», роль по замещению,
 * заместитель — сопровождение или не активный (в архиве, без пароля) — проверяет сервер (REC-006,
 * часовой пояс установки); пересечение — REC-005 с пересекающимся замещением в overlap.
 */
export const CreateSubstitutionBodySchema = z
  .object({
    assignmentId: z.uuid({ error: fieldMessages.assignment }),
    deputyUserId: z.uuid({ error: fieldMessages.deputy }),
    validFrom: z.iso.date({ error: fieldMessages.date }),
    validTo: z.iso.date({ error: fieldMessages.date }),
  })
  .refine((v) => datesInOrder(v.validFrom, v.validTo), {
    path: ['validTo'],
    error: fieldMessages.dateOrder,
  });
export type CreateSubstitutionBody = z.input<typeof CreateSubstitutionBodySchema>;

export const SubstitutionListSchema = z.array(SubstitutionSchema);

/** Страница списка замещений: { items, nextCursor }. */
export const SubstitutionsPageSchema = pageSchema(SubstitutionSchema);

/**
 * GET /api/substitutions/candidates (FR-018б): кандидаты в заместители — поиск по ФИО (не по
 * почте), курсор. Видит администратор клиента и владелец собственной роли, которую можно передать.
 */
export const SubstitutionCandidatesQuerySchema = z.object({
  ...PageQuerySchema.shape,
  q: z.string().trim().max(200).optional(),
});
export type SubstitutionCandidatesQuery = z.input<typeof SubstitutionCandidatesQuerySchema>;

/** Кандидат в заместители: только номер и ФИО — почты нет (FR-018б). */
export const SubstitutionCandidateSchema = z.strictObject({
  id: IdSchema,
  fullName: z.string(),
});
export type SubstitutionCandidate = z.infer<typeof SubstitutionCandidateSchema>;

/** Активные пользователи, кроме самого себя, по ФИО. */
export const SubstitutionCandidatesPageSchema = pageSchema(SubstitutionCandidateSchema);
